import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import React from 'react'

import type { ProductCategory, ProductListItem } from '@/api/admin-products-types'
import {
	useSetProductCategoryImage,
	useDeleteProductCategoryImage,
	useDeleteProductCategory
} from '@/api/api-mutations'
import {
	preloadGetProductCategory,
	preloadListProducts,
	useGetProductCategory,
	useGetProductCategoryHierarchy,
	useListProducts
} from '@/api/react-query'
import { AssetImageManager } from '@/components/asset-image-manager'
import { Badge } from '@/components/base/badge'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { SearchField } from '@/components/base/search-field'
import { SectionRow } from '@/components/base/section-row'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { GenericDetailSkeleton } from '@/components/skeletons/generic-detail-skeleton'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Trash } from '@/icons/trash'
import { PaginatedSearchSchema, ConditionalRouteComponent } from '@/utils/route-utilities'

import { useProductListColumnDefs } from '../products/-components'

export const Route = createFileRoute('/_authenticated/_general/product-categories/$categoryUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<CategoryDetail />
		</ConditionalRouteComponent>
	),
	pendingComponent: GenericDetailSkeleton,
	validateSearch: PaginatedSearchSchema,
	loaderDeps: ({ search: { limit, offset, q } }) => ({
		limit,
		offset,
		q
	}),
	loader: async ({ context, params: { categoryUuid }, deps: { limit, offset, q } }) => {
		preloadListProducts(context, {
			query: {
				limit,
				offset,
				q,
				categoryUuids: [categoryUuid]
			}
		})
		return await preloadGetProductCategory(context, { path: { categoryUuid } })
	},
	staticData: {
		crumb: ({ context: { L }, loaderData }) => loaderData?.name
	}
})

function CategoryDetail() {
	const { L } = useI18n()
	const { categoryUuid } = Route.useParams()
	const { limit = DEFAULT_PAGE_SIZE, offset, q } = Route.useSearch()
	const navigate = useNavigate()
	const prompt = usePrompt()

	const categoryResponse = useGetProductCategory({ categoryUuid })
	const category = categoryResponse.data
	const setImage = useSetProductCategoryImage()
	const deleteImage = useDeleteProductCategoryImage()
	const deleteCategory = useDeleteProductCategory()

	const handleDelete = async () => {
		const res = await prompt({
			title: L.ProductCategory.Delete.Title(),
			description: L.ProductCategory.Delete.Description({ name: category?.name ?? '' }),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})

		if (!res) {
			return
		}

		const result = await deleteCategory({ path: { categoryUuid } })
		if (result.isSuccess) {
			toast.success(L.ProductCategory.Delete.SuccessToast({ name: category?.name ?? '' }))
			navigate({ to: '/product-categories' })
		} else {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
		}
	}
	const { data: hierarchyInfo } = useGetProductCategoryHierarchy()
	const products = useListProducts({
		limit,
		offset,
		q,
		categoryUuids: [categoryUuid]
	})

	const columnDefs = useProductListColumnDefs()

	// Find parent category name from hierarchy
	const parentCategoryName = React.useMemo(() => {
		if (!category?.parentCategoryUuid || !hierarchyInfo) return null
		const findCategory = (categories: ProductCategory[]): string | null => {
			for (const cat of categories) {
				if (cat.uuid === category.parentCategoryUuid) return cat.name
				if (cat.children?.length) {
					const found = findCategory(cat.children)
					if (found) return found
				}
			}
			return null
		}
		return findCategory(hierarchyInfo.hierarchy)
	}, [category?.parentCategoryUuid, hierarchyInfo])

	return (
		<SingleColumnPage hasOutlet>
			{/* Category Info */}
			<Container className='divide-y'>
				<ContainerHeader
					heading={category?.name}
					rightSide={
						<Link from={Route.fullPath} to='edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
					actions={[
						{
							actions: [
								{
									label: L.General.Delete(),
									icon: <Trash />,
									onClick: handleDelete
								}
							]
						}
					]}
				/>
				{parentCategoryName && category?.parentCategoryUuid && (
					<SectionRow
						title={L.ProductCategory.ParentCategory()}
						value={
							// Same treatment as the product detail page's group/category rows: `SectionRow`
							// only applies its small text style to string values, so a bare Link renders at
							// the page's default size. A Badge keeps it in line with every other row value.
							<Badge size='2xsmall' className='block w-fit truncate' asChild>
								<Link
									to='/product-categories/$categoryUuid'
									params={{ categoryUuid: category.parentCategoryUuid }}
								>
									{parentCategoryName}
								</Link>
							</Badge>
						}
					/>
				)}
			</Container>

			{/* Category Image */}
			<AssetImageManager
				image={category?.image}
				labels={{
					title: L.ProductCategory.Image.Title(),
					emptyState: L.ProductCategory.Image.EmptyState(),
					upload: L.ProductCategory.Image.Upload(),
					chooseFromLibrary: L.ProductCategory.Image.ChooseFromLibrary(),
					remove: L.ProductCategory.Image.Remove(),
					libraryPickerTitle: L.ProductCategory.Image.LibraryPickerTitle(),
					libraryPickerEmpty: L.ProductCategory.Image.LibraryPickerEmpty(),
					libraryPickerAllAttached: L.ProductCategory.Image.LibraryPickerAllAttached(),
					uploadFailedToast: L.ProductCategory.Image.UploadFailedToast()
				}}
				onSetAsset={async assetUuid => {
					await setImage({ path: { categoryUuid }, body: { assetUuid } })
					toast.success(L.ProductCategory.Image.SetSuccessToast())
				}}
				onRemove={async () => {
					await deleteImage({ path: { categoryUuid } })
					toast.success(L.ProductCategory.Image.RemoveSuccessToast())
				}}
			/>

			{/* Products in Category */}
			<Container className='flex flex-col'>
				<ContainerHeader heading={L.ProductCategory.ProductsTitle()} rightSide={<SearchField />} />
				<div className='min-w-0 overflow-x-auto'>
					<Table<ProductListItem>
						className='w-full min-w-[600px]'
						data={products.data?.products}
						columnDefs={columnDefs}
						animateRows
						domLayout='autoHeight'
						suppressCellFocus
						rowToRouterConfig={row => ({
							to: '/products/$productUuid',
							params: { productUuid: row.uuid }
						})}
					/>
				</div>
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={products.data?.count} />
				</div>
			</Container>
		</SingleColumnPage>
	)
}
